import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  params, string, shape, bool, useDataModel,
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import classNames from 'classnames/bind';
import { getCustomUrlWithAnalytics } from '../../productDetailsUtils';
import styles from './ProductHeader.module.scss';

const CONFIGURABLE_BLINDS = 'CONFIGURABLE_BLINDS';
const BUILD_AND_BUY = 'Build and Buy';
const cx = classNames.bind(styles);
export const ProductHeader = ({
  itemId,
  brand,
  collectionBlock,
  disableShopThisCollection,
  large,
  target,
  brandMaxLine,
  brandTitleMaxLine,
  titleMaxLine,
  hover,
  fullWidth,
  xSmall,
  onClick
}) => {
  const {
    channel,
    hosts
  } = useContext(ExperienceContext);

  const CustomHeaderTag = useConfigService('fs:specialBuyHeaderTag') ? 'div' : 'h3';

  const { data } = useDataModel('product', {
    variables: {
      itemId
    }
  });

  const { product } = data || {};

  const {
    details,
    identifiers,
    info
  } = product || {};

  const {
    collection: collectionObj
  } = details || {};

  const {
    name: collectionName,
    url: collectionUrl
  } = collectionObj || {};

  const {
    canonicalUrl,
    brandName,
    productType = '',
    productLabel
  } = identifiers || {};

  if (!productLabel) return null;

  // @TODO update analytics based on new approach
  const publishAnalytics = () => {};

  const shouldDisplayBrand = (location) => {
    return brandName && brand === location;
  };

  const configureProduct = productType === CONFIGURABLE_BLINDS;
  const customExperience = info?.globalCustomConfigurator?.customExperience;
  /* Using hover to handle the differences between hover (hd home) and GM. */
  const isBuildAndBuyProduct = hover ? customExperience && configureProduct : configureProduct;

  const isMobile = channel === 'mobile';
  const showShopThisCollectionLink = !disableShopThisCollection && collectionName && collectionUrl;
  const onBuildAndBuyImageClick = () => publishAnalytics('product-pod.click', BUILD_AND_BUY);
  const checkOnClick = isBuildAndBuyProduct ? onBuildAndBuyImageClick : onClick;

  const productURL = getCustomUrlWithAnalytics({
    canonicalUrl,
    blindsHost: hosts?.customBlinds,
    hover,
    configureProduct,
    customExperience,
    info
  });

  const headerTarget = isMobile ? '' : target;

  const titleClasses = cx('product-header__title', {
    'product-header__title-product': (brand === 'inline' || brand === 'above'),
    'product-header__title--large': large,
    'product-header__title--full-width': fullWidth
  });
  const labelClasses = cx('product-header__title-product', {
    'product-header__title-product--twoline': titleMaxLine === 2,
    'product-header__title-product--xsmall': xSmall && isMobile
  });
  const brandClasses = cx('product-header__title__brand', {
    'product-header__title__brand--oneline': brandMaxLine === 1,
    'product-header__title__brand--xsmall': xSmall && isMobile
  });
  const shopThisCollectionClasses = cx('product-header__title__collection', {
    'product-header__title__collection--block': brand === 'inline'
      && collectionBlock && !hover
  });
  const brandTitleClasses = cx({
    'product-header__title--control': !brandTitleMaxLine,
    'product-header__title--clamp': !!brandTitleMaxLine,
    'product-header__title--twoline': brandTitleMaxLine === 2,
    'product-header__title--threeline': brandTitleMaxLine === 3,
    'product-header__title--fourline': brandTitleMaxLine === 4
  });

  return (
    <>
      <div data-testid="product-header">
        <a
          href={productURL}
          onClick={checkOnClick}
          target={headerTarget}
        >
          <div className={brandTitleClasses}>
            <CustomHeaderTag className={titleClasses}>
              {shouldDisplayBrand('above')
                && (
                  <p className={cx('product-header__title__brand--bold')}> {brandName} </p>
                )}
              {shouldDisplayBrand('inline')
                && (
                  <span className={cx('product-header__title__brand--bold')}>
                    {brandName}
                  </span>
                )}
              <span className={labelClasses}>
                {productLabel}
              </span>
            </CustomHeaderTag>
          </div>
        </a>
        {shouldDisplayBrand('below')
          && (
            <p className={brandClasses}>by
              <span className={cx('product-header__title__brand--bold')}> {brandName}</span>
            </p>
          )}
      </div>
      {showShopThisCollectionLink && (
        <a className={shopThisCollectionClasses} href={collectionUrl} target={target}>
          Shop this Collection
        </a>
      )}
    </>
  );
};

ProductHeader.propTypes = {
  itemId: PropTypes.string.isRequired,
  /** Position the brand name */
  brand: PropTypes.oneOf(['above', 'below', 'hidden', 'inline']),
  collectionBlock: PropTypes.bool,
  disableShopThisCollection: PropTypes.bool,
  /** Larger text for title */
  large: PropTypes.bool,
  target: PropTypes.string,
  fullWidth: PropTypes.bool,
  titleMaxLine: PropTypes.number,
  brandMaxLine: PropTypes.number,
  brandTitleMaxLine: PropTypes.number,
  xSmall: PropTypes.bool,
  onClick: PropTypes.func,
  hover: PropTypes.bool
};

ProductHeader.defaultProps = {
  brand: 'below',
  collectionBlock: true,
  disableShopThisCollection: false,
  large: false,
  target: null,
  fullWidth: false,
  titleMaxLine: null,
  brandMaxLine: null,
  brandTitleMaxLine: null,
  xSmall: false,
  onClick: () => {},
  hover: false
};

ProductHeader.displayName = 'ProductHeader';

ProductHeader.dataModel = {
  product: params({
    itemId: string().isRequired(),
  }).shape({
    itemId: string(),
    dataSource: string(),
    details: shape({
      collection: shape({
        name: string(),
        url: string()
      })
    }),
    identifiers: shape({
      canonicalUrl: string(),
      brandName: string(),
      productType: string(),
      productLabel: string()
    }),
    info: shape({
      isSponsored: bool(),
      sponsoredMetadata: shape({
        campaignId: string(),
        placementId: string(),
        slotId: string()
      }),
      globalCustomConfigurator: shape({
        customExperience: string()
      })
    })
  })
};
